import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

// Polls the backend after the Stripe checkout success redirect.
// The webhook is the source of truth, but we poll /billing/me to update the UI
// once the webhook has been processed.
const POLL_MS = 1500;
const MAX_POLLS = 8;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser, user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | active | timeout
  const [me, setMe] = useState(null);

  useEffect(() => {
    document.title = lang === "es" ? "Suscripción activada · GalascioDAS™" : "Subscription activated · GalascioDAS™";
  }, [lang]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    // Only poll the checkout status endpoint on the first attempt to avoid
    // flooding the console with 404s if the session_id is invalid.
    let triedStatus = false;

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        if (sessionId && !triedStatus) {
          triedStatus = true;
          await api.get(`/billing/checkout/status/${sessionId}`).catch(() => null);
        }
        const r = await api.get("/billing/me");
        if (cancelled) return;
        setMe(r.data);
        if (r.data?.is_premium) {
          setStatus("active");
          if (refreshUser) await refreshUser();
          return;
        }
      } catch (e) {
        /* ignore transient errors */
      }
      if (attempts >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, user]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <Header />
      <main
        data-testid="billing-success-page"
        className="max-w-2xl mx-auto px-6 md:px-10 py-20 text-center"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#D90429] mb-6" />
            <h1 className="font-serif-display text-3xl md:text-4xl font-bold mb-3">
              {lang === "es" ? "Confirmando tu pago…" : "Confirming your payment…"}
            </h1>
            <p className="text-base text-[#525252] font-serif-display italic">
              {t("billing_success_lead")}
            </p>
          </>
        )}
        {status === "active" && (
          <>
            <div className="w-16 h-16 mx-auto bg-[#D90429] text-white flex items-center justify-center mb-6">
              <Check className="w-8 h-8" strokeWidth={3} />
            </div>
            <h1
              data-testid="billing-success-title"
              className="font-serif-display text-4xl md:text-5xl font-bold mb-3"
            >
              {t("billing_success_title")}
            </h1>
            <p className="text-base text-[#404040] font-serif-display italic mb-3">
              {lang === "es" ? "Tu plan ahora es" : "Your plan is now"}:{" "}
              <strong className="text-[#0A0A0A] not-italic">
                {t(`plan_${me?.plan}_name`) || me?.plan}
              </strong>
            </p>
            <div className="mt-8 flex gap-3 justify-center flex-wrap">
              <Link
                to="/app"
                data-testid="billing-success-go-app"
                className="px-6 py-3 bg-[#0A0A0A] text-white hover:bg-[#D90429] font-mono-tech text-xs uppercase tracking-[0.2em]"
              >
                {lang === "es" ? "Ir al panel" : "Go to workspace"} →
              </Link>
              <Link
                to="/app/billing"
                className="px-6 py-3 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-mono-tech text-xs uppercase tracking-[0.2em]"
              >
                {t("billing_title")}
              </Link>
            </div>
          </>
        )}
        {status === "timeout" && (
          <>
            <h1 className="font-serif-display text-3xl md:text-4xl font-bold mb-3">
              {lang === "es" ? "Procesando…" : "Processing…"}
            </h1>
            <p className="text-base text-[#525252] font-serif-display italic mb-6">
              {t("billing_success_lead")}
            </p>
            <button
              onClick={() => window.location.reload()}
              data-testid="billing-success-refresh"
              className="px-6 py-3 bg-[#D90429] text-white hover:bg-[#A60023] font-mono-tech text-xs uppercase tracking-[0.2em]"
            >
              {t("billing_success_refresh")}
            </button>
          </>
        )}
      </main>
    </div>
  );
}
